import { featureToWorld, worldToFeature } from '../utils/coordinateTransforms.js';

/**
 * FeatureChangeService - Re-references program nodes from one feature to another
 *
 * "Keep absolute position" means every pose the node owns has to be re-expressed
 * in the new frame:
 *
 *     newLocalPose = inverse(T_newFeature) * T_oldFeature * oldLocalPose
 *
 * This is node-driven rather than waypoint-driven. A node's <dataModel> can hold
 * several kinds of Pose, and transforming only the way-point leaves the rest
 * behind in the old frame - which is silent corruption, not a visible error.
 *
 * React-free so it can be unit tested and reused outside the UI.
 */

/**
 * Poses stored in the owning node's OWN feature frame. Every one of these has to
 * be transformed when the node is re-pointed at a new feature.
 *
 * - way-point       the waypoint itself
 * - via-point       mid-point of a circular move (motion-type="C")
 * - pattern-origin  origin and X-direction of a Pattern node. These define the
 * - pattern-xdir    direction the pattern steps: miss them and the pattern runs
 *                   the wrong way while every individual waypoint still looks
 *                   correct. If the two features differ by 180 degrees it steps
 *                   in exactly the opposite direction.
 * - start-point     the two ends of a SearchPartL touch-search.
 * - contact-point
 */
export const FEATURE_RELATIVE_POSE_KEYS = Object.freeze([
  'way-point',
  'via-point',
  'pattern-origin',
  'pattern-xdir',
  'start-point',
  'contact-point',
]);

/**
 * Poses stored in ROBOT BASE coordinates - these must never be transformed.
 * They are the three taught points a Toolbox "FeatureNodeName" node uses to
 * define a feature, and they live in a node that carries no feature tag at all.
 * That absence is the signal: a node WITH a feature tag declares that the poses
 * in its dataModel are expressed in that feature.
 */
export const ABSOLUTE_POSE_KEYS = Object.freeze([
  'feature-center',
  'feature-x',
  'feature-y',
]);

const POSITION_KEYS = ['X', 'Y', 'Z'];
const ROTATION_KEYS = ['RX', 'RY', 'RZ'];

/** Largest world-space movement we tolerate before calling the result wrong. */
const DRIFT_TOLERANCE_M = 1e-9;

/**
 * The element that owns a dataModel, whether or not it is suppressed.
 *
 * A node disabled in PolyScope is stored as
 * <suppressedNode class="Contributed" strategyProgramNodeType="...">, keeping an
 * identical dataModel. Matching only 'Contributed' walks straight past it and
 * leaves its waypoints in the old frame, so they land somewhere wrong if the
 * node is ever re-enabled.
 * @param {Element} element - Any element inside the node
 * @returns {Element|null} The owning node element
 */
export function closestNode(element) {
  return element?.closest('Contributed, suppressedNode') || null;
}

/**
 * Human-readable name for a node element.
 * @param {Element} node - Node element
 * @returns {string}
 */
export function nodeLabel(node) {
  if (!node) return 'unknown';
  const name = node.getAttribute('strategyProgramNodeType') || node.tagName;
  return node.tagName === 'suppressedNode' ? `${name} (suppressed)` : name;
}

/**
 * A node's own feature element - its dataModel's, never a descendant's.
 * @param {Element} node - Node element
 * @returns {Element|null}
 */
export function ownFeatureElement(node) {
  const dataModel = node?.querySelector(':scope > dataModel');
  if (!dataModel) return null;
  return dataModel.querySelector(':scope > data[key="feature"]');
}

/**
 * Every Pose the node owns directly (not its children's).
 * @param {Element} node - Node element
 * @returns {Array<Element>}
 */
export function ownPoses(node) {
  const dataModel = node?.querySelector(':scope > dataModel');
  if (!dataModel) return [];
  return Array.from(dataModel.querySelectorAll(':scope > Pose'));
}

/**
 * Read a Pose element.
 * @param {Element} poseElement - Pose element
 * @returns {{position: {x, y, z}, rotation: {rx, ry, rz}}}
 */
export function readPose(poseElement) {
  const positionElement = poseElement.querySelector('Position[key="posePosition"]');
  const rotationElement = poseElement.querySelector('Rotation[key="poseRotation"]');
  if (!positionElement || !rotationElement) {
    throw new Error('Pose is missing posePosition or poseRotation');
  }

  const position = {};
  POSITION_KEYS.forEach((key, i) => {
    const node = positionElement.querySelector(`Length[key="${key}"]`);
    if (!node) throw new Error(`Pose is missing Length[key="${key}"]`);
    position[['x', 'y', 'z'][i]] = parseFloat(node.getAttribute('value'));
  });

  const rotation = {};
  ROTATION_KEYS.forEach((key, i) => {
    const node = rotationElement.querySelector(`Angle[key="${key}"]`);
    if (!node) throw new Error(`Pose is missing Angle[key="${key}"]`);
    rotation[['rx', 'ry', 'rz'][i]] = parseFloat(node.getAttribute('value'));
  });

  return { position, rotation };
}

/**
 * Write a Pose element, touching only the six value attributes.
 * @param {Element} poseElement - Pose element
 * @param {{x, y, z}} position - New position
 * @param {{rx, ry, rz}} rotation - New rotation
 */
export function writePose(poseElement, position, rotation) {
  const positionElement = poseElement.querySelector('Position[key="posePosition"]');
  const rotationElement = poseElement.querySelector('Rotation[key="poseRotation"]');

  POSITION_KEYS.forEach((key, i) => {
    const value = position[['x', 'y', 'z'][i]];
    positionElement.querySelector(`Length[key="${key}"]`).setAttribute('value', value.toString());
  });
  ROTATION_KEYS.forEach((key, i) => {
    const value = rotation[['rx', 'ry', 'rz'][i]];
    rotationElement.querySelector(`Angle[key="${key}"]`).setAttribute('value', value.toString());
  });
}

/**
 * Re-express a pose in a different feature frame, keeping it physically put.
 * @param {{x, y, z}} position - Position in the source feature's frame
 * @param {{rx, ry, rz}} rotation - Rotation in the source feature's frame
 * @param {Object} fromFeature - Source feature
 * @param {Object} toFeature - Target feature
 * @returns {{position, rotation}} Pose in the target feature's frame
 */
export function rebasePose(position, rotation, fromFeature, toFeature) {
  const world = featureToWorld(position, rotation, fromFeature);
  return worldToFeature(world.position, world.rotation, toFeature);
}

/**
 * Change the feature a set of nodes references.
 *
 * @param {Array<Element>} nodeElements - Nodes to convert (Contributed or suppressedNode)
 * @param {Object} toFeature - Target feature {name, position, rotation}
 * @param {Array<Object>} features - All known features, to resolve each node's source frame
 * @param {string} transformMode - 'absolute' keeps the physical location, 'relative' only retags
 * @returns {{converted, viaPoints, patternPoses, searchPoses, suppressed, nodesRetagged,
 *            skipped, maxDrift, warnings}} What happened
 * @throws {Error} If a node holds a Pose whose frame we cannot determine
 */
export function changeFeatureForNodes(nodeElements, toFeature, features, transformMode = 'absolute') {
  const result = {
    converted: 0,
    viaPoints: 0,
    patternPoses: 0,
    searchPoses: 0,
    suppressed: 0,
    nodesRetagged: 0,
    skipped: 0,
    maxDrift: 0,
    warnings: [],
  };

  const featuresByName = new Map((features || []).map((f) => [f.name, f]));
  const unresolved = new Set();

  // Validate every node up front. A half-converted program is worse than an
  // unconverted one, so an unknown pose key aborts before anything is written.
  nodeElements.forEach((node) => {
    const featureElement = ownFeatureElement(node);
    if (!featureElement) return;

    const unknown = ownPoses(node)
      .map((pose) => pose.getAttribute('key'))
      .filter((key) => !FEATURE_RELATIVE_POSE_KEYS.includes(key)
                    && !ABSOLUTE_POSE_KEYS.includes(key));

    if (unknown.length > 0) {
      throw new Error(
        `${nodeLabel(node)} node holds unrecognised pose(s) `
        + `${[...new Set(unknown)].sort().join(', ')} - refusing to retag it without `
        + 'knowing which frame they are in. Classify the key in '
        + 'FEATURE_RELATIVE_POSE_KEYS or ABSOLUTE_POSE_KEYS in FeatureChangeService.js.'
      );
    }
  });

  nodeElements.forEach((node) => {
    const featureElement = ownFeatureElement(node);
    if (!featureElement) {
      result.skipped++;
      return;
    }

    const currentName = featureElement.getAttribute('value');
    if (currentName === toFeature.name) return; // already where we want it

    if (transformMode === 'absolute') {
      const fromFeature = featuresByName.get(currentName);
      if (!fromFeature) {
        // We cannot transform out of a frame we do not know. Leave the node
        // exactly as it is - its poses stay physically correct, they just do
        // not get consolidated.
        unresolved.add(currentName);
        result.skipped++;
        return;
      }

      ownPoses(node).forEach((poseElement) => {
        const key = poseElement.getAttribute('key');
        if (ABSOLUTE_POSE_KEYS.includes(key)) return;

        const { position, rotation } = readPose(poseElement);
        const moved = rebasePose(position, rotation, fromFeature, toFeature);
        writePose(poseElement, moved.position, moved.rotation);

        // Self-check: the pose must not move in the world. Verify against what
        // was actually written back, not against what we intended to write.
        const written = readPose(poseElement);
        const was = featureToWorld(position, rotation, fromFeature);
        const now = featureToWorld(written.position, written.rotation, toFeature);
        result.maxDrift = Math.max(
          result.maxDrift,
          Math.abs(was.position.x - now.position.x),
          Math.abs(was.position.y - now.position.y),
          Math.abs(was.position.z - now.position.z)
        );

        if (key === 'way-point') result.converted++;
        else if (key === 'via-point') result.viaPoints++;
        else if (key === 'start-point' || key === 'contact-point') result.searchPoses++;
        else result.patternPoses++;
      });
    } else {
      // 'relative': coordinates stay, so the node physically moves with the
      // feature. Only the tag changes.
      result.converted += ownPoses(node)
        .filter((p) => p.getAttribute('key') === 'way-point').length;
    }

    featureElement.setAttribute('value', toFeature.name);
    result.nodesRetagged++;
    if (node.tagName === 'suppressedNode') result.suppressed++;
  });

  unresolved.forEach((name) => {
    result.warnings.push(
      `Feature "${name}" is referenced by the program but not defined in the `
      + 'installation, so those nodes were left untouched.'
    );
  });

  if (result.maxDrift > DRIFT_TOLERANCE_M) {
    throw new Error(
      `Feature change moved a waypoint ${result.maxDrift.toExponential(2)} m in world `
      + 'space; expected it to stay put. The file has not been changed correctly.'
    );
  }

  return result;
}

/**
 * Every node in the document that owns a feature-bearing dataModel.
 *
 * Scans dataModels rather than Contributed elements so suppressed nodes are
 * included.
 * @param {Document} xmlDoc - The program document
 * @returns {Array<Element>} Node elements in document order
 */
export function findFeatureNodes(xmlDoc) {
  if (!xmlDoc) return [];
  return Array.from(xmlDoc.querySelectorAll('dataModel'))
    .map((dataModel) => dataModel.parentElement)
    .filter((node) => node && ownFeatureElement(node));
}

/**
 * The nodes owning the given waypoints, plus any ancestor or descendant node
 * whose own feature reference would otherwise be left behind.
 *
 * A Weld wrapper and its waypoint-less Approach/Depart children each carry their
 * own feature tag. They are included only when every waypoint under the wrapper
 * is part of this change, so a partial selection never silently drags along
 * nodes the user did not pick.
 *
 * @param {Document} xmlDoc - The program document
 * @param {Array<Element>} poseElements - The selected way-point Pose elements
 * @returns {{nodes: Array<Element>, partialWrappers: Array<Element>}}
 */
export function nodesForWaypoints(xmlDoc, poseElements) {
  const selectedNodes = new Set();
  poseElements.forEach((pose) => {
    const node = closestNode(pose);
    if (node) selectedNodes.add(node);
  });

  const nodes = new Set(selectedNodes);
  const partialWrappers = [];

  findFeatureNodes(xmlDoc).forEach((node) => {
    if (nodes.has(node)) return;

    const waypointsBelow = Array.from(node.querySelectorAll('Pose[key="way-point"]'));
    const ownsWaypoints = ownPoses(node).some((p) => p.getAttribute('key') === 'way-point');
    if (ownsWaypoints) return; // a leaf, handled above if selected

    if (waypointsBelow.length > 0) {
      // A wrapper: include it only if the whole group is moving.
      const allSelected = waypointsBelow.every((p) => selectedNodes.has(closestNode(p)));
      if (allSelected) nodes.add(node);
      else partialWrappers.push(node);
      return;
    }

    // No waypoints at all (Approach/Depart). It follows its enclosing wrapper.
    const parentNode = closestNode(node.parentElement);
    if (parentNode && nodes.has(parentNode)) nodes.add(node);
  });

  // Preserve document order so counts and messages read predictably.
  return {
    nodes: findFeatureNodes(xmlDoc).filter((n) => nodes.has(n)),
    partialWrappers,
  };
}
